'use client'

import Link from 'next/link'
import { forwardRef } from 'react'
import { cn } from '@/utilities/ui'
import { getLinkHref, extractLinkData, type PayloadLinkData } from '@/components/Link'

// Button variants
export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'link' | 'filter'

// Button sizes
export type ButtonSize = 'sm' | 'md' | 'lg'

// Button colors
export type ButtonColor = 'brand' | 'white' | 'dark' | 'gray' | 'success' | 'warning' | 'error'

// Button props interface
export interface ButtonProps {
  // Content
  children?: React.ReactNode
  text?: string

  // Link properties - accept either PayloadCMS link object or simple href string
  link?: PayloadLinkData & { enabled?: boolean | null }
  href?: string
  external?: boolean

  // Styling
  variant?: ButtonVariant
  size?: ButtonSize
  color?: ButtonColor
  className?: string

  // State
  disabled?: boolean
  loading?: boolean
  isActive?: boolean

  // Behavior
  onClick?: () => void
  type?: 'button' | 'submit' | 'reset'

  // Accessibility
  'aria-label'?: string
  'aria-describedby'?: string
}

// Color schemes for different variants and colors
const colorSchemes = {
  brand: {
    primary: 'bg-brand text-text-white hover:bg-brand/80 border-border-brand',
    secondary: 'bg-brand/10 text-text-brand hover:bg-brand/20 border-border-brand/20',
    outline: 'bg-transparent text-text-brand hover:bg-brand/10 border-border-brand',
    ghost: 'bg-transparent text-text-brand hover:bg-brand/10 border-transparent',
    link: 'bg-transparent text-text-brand hover:text-brand/80 border-transparent',
    filter: 'bg-transparent text-text-primary hover:text-text-primary border-transparent',
  },
  white: {
    primary: 'bg-white text-text-primary hover:bg-gray-100 border-white',
    secondary: 'bg-white/10 text-text-white hover:bg-white/20 border-white/20',
    outline: 'bg-transparent text-text-white hover:bg-white/10 border-white',
    ghost: 'bg-transparent text-text-white hover:bg-white/10 border-transparent',
    link: 'bg-transparent text-text-white hover:text-gray-200 border-transparent',
    filter: 'bg-transparent text-text-white hover:text-text-white border-transparent',
  },
  dark: {
    primary: 'bg-dark text-text-white hover:bg-dark/80 border-dark',
    secondary: 'bg-dark/10 text-text-primary hover:bg-dark/20 border-dark/20',
    outline: 'bg-transparent text-text-primary hover:bg-dark/10 border-dark',
    ghost: 'bg-transparent text-text-primary hover:bg-dark/10 border-transparent',
    link: 'bg-transparent text-text-primary hover:text-dark/80 border-transparent',
    filter: 'bg-transparent text-text-primary hover:text-text-primary border-transparent',
  },
  gray: {
    primary: 'bg-gray-600 text-white hover:bg-gray-700 border-gray-600',
    secondary: 'bg-gray-600/10 text-gray-600 hover:bg-gray-600/20 border-gray-600/20',
    outline: 'bg-transparent text-gray-600 hover:bg-gray-600/10 border-gray-600',
    ghost: 'bg-transparent text-gray-600 hover:bg-gray-600/10 border-transparent',
    link: 'bg-transparent text-gray-600 hover:text-gray-700 border-transparent',
    filter: 'bg-transparent text-gray-600 hover:text-gray-600 border-transparent',
  },
  success: {
    primary: 'bg-green-600 text-white hover:bg-green-700 border-green-600',
    secondary: 'bg-green-600/10 text-green-600 hover:bg-green-600/20 border-green-600/20',
    outline: 'bg-transparent text-green-600 hover:bg-green-600/10 border-green-600',
    ghost: 'bg-transparent text-green-600 hover:bg-green-600/10 border-transparent',
    link: 'bg-transparent text-green-600 hover:text-green-700 border-transparent',
    filter: 'bg-transparent text-green-600 hover:text-green-600 border-transparent',
  },
  warning: {
    primary: 'bg-yellow-600 text-white hover:bg-yellow-700 border-yellow-600',
    secondary: 'bg-yellow-600/10 text-yellow-600 hover:bg-yellow-600/20 border-yellow-600/20',
    outline: 'bg-transparent text-yellow-600 hover:bg-yellow-600/10 border-yellow-600',
    ghost: 'bg-transparent text-yellow-600 hover:bg-yellow-600/10 border-transparent',
    link: 'bg-transparent text-yellow-600 hover:text-yellow-700 border-transparent',
    filter: 'bg-transparent text-yellow-600 hover:text-yellow-600 border-transparent',
  },
  error: {
    primary: 'bg-red-600 text-white hover:bg-red-700 border-red-600',
    secondary: 'bg-red-600/10 text-red-600 hover:bg-red-600/20 border-red-600/20',
    outline: 'bg-transparent text-red-600 hover:bg-red-600/10 border-red-600',
    ghost: 'bg-transparent text-red-600 hover:bg-red-600/10 border-transparent',
    link: 'bg-transparent text-red-600 hover:text-red-700 border-transparent',
    filter: 'bg-transparent text-red-600 hover:text-red-600 border-transparent',
  },
}

// Size classes
const sizeClasses = {
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg',
}

// Base button classes
const baseClasses =
  'inline-flex items-center justify-center gap-2 font-ui font-medium transition-all duration-300 focus:outline-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed'

// Variant-specific classes
const variantClasses = {
  filter: 'px-0 py-2 border-b border-text-primary',
}

export const LinkWithIcon = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (
    {
      children,
      text,
      link,
      href: hrefProp,
      external = false,
      variant = 'link',
      size = 'md',
      color = 'brand',
      className = '',
      disabled = false,
      loading = false,
      isActive = false,
      onClick,
      type = 'button',
      'aria-label': ariaLabel,
      'aria-describedby': ariaDescribedBy,
      ...props
    },
    ref,
  ) => {
    // Handle PayloadCMS link object - extract href and check enabled state
    // If link.enabled is false, don't render the component
    if (link && link.enabled === false) {
      return null
    }

    // Extract href from link object or use provided href prop
    const href = hrefProp || (link ? getLinkHref(extractLinkData(link)) : null)

    // Determine content to display - use text prop, link.label, or children
    const content = text || link?.label || children

    // Determine if link should open in new tab
    const shouldOpenInNewTab = external || link?.openInNewTab || false

    // Get color scheme
    const colorScheme = colorSchemes[color][variant]

    // Get variant-specific classes
    const variantClass = variantClasses[variant as keyof typeof variantClasses] || ''

    // Handle active state for filter variant
    const activeClass =
      variant === 'filter' && isActive ? 'border-b-2 border-text-primary font-medium' : ''

    // Build class names
    const buttonClasses = cn(
      baseClasses,
      sizeClasses[size],
      variantClass,
      colorScheme,
      activeClass,
      {
        'cursor-not-allowed': disabled || loading,
        'pointer-events-none': disabled || loading,
      },
      className,
    )

    // Loading spinner component
    const LoadingSpinner = () => (
      <svg
        className="animate-spin -ml-1 mr-2 h-4 w-4"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    )

    // Arrow icon component
    const ArrowIcon = () => (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="transition-transform duration-300 group-hover:translate-x-1"
      >
        <path
          d="M9 18L15 12L9 6"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )

    // If it's a link (has href)
    if (href) {
      const linkProps = {
        href,
        className: cn(buttonClasses, 'group'),
        'aria-label': ariaLabel,
        'aria-describedby': ariaDescribedBy,
        ...(shouldOpenInNewTab && { target: '_blank', rel: 'noopener noreferrer' }),
        ...props,
      }

      return (
        <Link {...linkProps} ref={ref as React.Ref<HTMLAnchorElement>}>
          {loading && <LoadingSpinner />}
          <span className="uppercase">{content}</span>
          {!loading && <ArrowIcon />}
        </Link>
      )
    }

    // If it's a button
    const buttonProps = {
      type,
      className: buttonClasses,
      disabled: disabled || loading,
      onClick,
      'aria-label': ariaLabel,
      'aria-describedby': ariaDescribedBy,
      ...props,
    }

    return (
      <button {...buttonProps} ref={ref as React.Ref<HTMLButtonElement>}>
        {loading && <LoadingSpinner />}
        <span className="uppercase">{content}</span>
        {!loading && variant === 'link' && <ArrowIcon />}
      </button>
    )
  },
)

LinkWithIcon.displayName = 'LinkWithIcon'

export default LinkWithIcon
